import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/auth/auth-provider';
import { ProtectedRoute } from '@/auth/protected-route';
import { AppShell } from '@/components/layout';
import { DemoProvider } from '@/demo/demo-context';
import { DemoBanner } from '@/demo/demo-banner';
import { RootRedirect } from '@/components/root-redirect';
import {
  LoginPage,
  DemoLandingPage,
  HomePage,
  ValuesPage,
  GoalsPage,
  EntriesPage,
  EntryDetailPage,
  TasksPage,
  FocusPage,
  ReviewPage,
  TopicsPage,
  AssigneesPage,
  SearchPage,
  ProfilePage,
  SettingsPage,
  IntegrationsPage,
  BingoPage,
  BingoDetailPage,
  VoiceCapturePage,
} from '@/pages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DemoProvider>
          <BrowserRouter>
            <Routes>
              {/* Root - smart redirect based on auth status */}
              <Route path="/" element={<RootRedirect />} />
              
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/demo" element={<DemoLandingPage />} />
              
              {/* Demo mode routes (public, no auth required) */}
              <Route path="/demo/:profileId">
                <Route element={<AppShell demoBanner={<DemoBanner />} />}>
                  <Route index element={<HomePage />} />
                  <Route path="values" element={<ValuesPage />} />
                  <Route path="goals" element={<GoalsPage />} />
                  <Route path="entries" element={<EntriesPage />} />
                  <Route path="tasks" element={<TasksPage />} />
                  <Route path="focus" element={<FocusPage />} />
                  <Route path="review" element={<ReviewPage />} />
                  <Route path="topics" element={<TopicsPage />} />
                  <Route path="assignees" element={<AssigneesPage />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="integrations" element={<IntegrationsPage />} />
                  <Route path="bingo" element={<BingoPage />} />
                  <Route path="voice-capture" element={<VoiceCapturePage />} />
                </Route>
                
                {/* Full-screen demo routes */}
                <Route path="entries/:id" element={<EntryDetailPage />} />
                <Route path="bingo/:id" element={<BingoDetailPage />} />
              </Route>

              {/* Protected routes (authenticated users) */}
              <Route element={<ProtectedRoute />}>
                <Route element={<AppShell />}>
                  <Route path="home" element={<HomePage />} />
                  <Route path="values" element={<ValuesPage />} />
                  <Route path="goals" element={<GoalsPage />} />
                  <Route path="entries" element={<EntriesPage />} />
                  <Route path="tasks" element={<TasksPage />} />
                  <Route path="focus" element={<FocusPage />} />
                  <Route path="review" element={<ReviewPage />} />
                  <Route path="topics" element={<TopicsPage />} />
                  <Route path="assignees" element={<AssigneesPage />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="integrations" element={<IntegrationsPage />} />
                  <Route path="bingo" element={<BingoPage />} />
                  <Route path="voice-capture" element={<VoiceCapturePage />} />
                </Route>

                {/* Full-screen routes (no shell) */}
                <Route
                  path="entries/:id"
                  element={<EntryDetailPage />}
                />
                <Route
                  path="bingo/:id"
                  element={<BingoDetailPage />}
                />
              </Route>

              {/* Fallback - redirect to demo landing */}
              <Route path="*" element={<Navigate to="/demo" replace />} />
            </Routes>
          </BrowserRouter>
        </DemoProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
